import * as THREE from 'three';
import type { BulletVisual } from '../content/types';
import { bulletGeometry, mergeGeometries } from './geometry';

export interface EnemyProjectileLook {
  id: string;
  geometry: THREE.BufferGeometry;
  width: number;
  length: number;
  spin: number;
  trailLength: number;
  trailOpacity: number;
  impactCount: number;
  /** Reserved for a future free/CC0 authored model replacement. */
  gltf?: string;
}

function part(geometry: THREE.BufferGeometry, scale: [number, number, number], z = 0, rx = 0): THREE.BufferGeometry {
  const copy = geometry.clone();
  copy.scale(...scale);
  copy.rotateX(rx);
  copy.translate(0, 0, z);
  return copy;
}

const ERA_LOOKS: EnemyProjectileLook[] = [
  { id: 'primordial-spore', geometry: mergeGeometries([part(new THREE.IcosahedronGeometry(.38, 1), [1, 1, 1]), part(new THREE.SphereGeometry(.16, 6, 4), [1, 1, 1], .34)]), width: 1.2, length: 1.2, spin: 5, trailLength: 1.5, trailOpacity: .24, impactCount: 10 },
  { id: 'cambrian-shell', geometry: mergeGeometries([part(new THREE.ConeGeometry(.38, .75, 5), [1, 1, 1], .15, Math.PI / 2), part(new THREE.TorusGeometry(.28, .07, 5, 16), [1, 1, 1], -.25)]), width: 1.1, length: 1.45, spin: 7, trailLength: 1.7, trailOpacity: .22, impactCount: 11 },
  { id: 'dinosaur-fang', geometry: part(new THREE.ConeGeometry(.32, 1.25, 5), [1, 1, 1], 0, Math.PI / 2), width: .8, length: 1.65, spin: 0, trailLength: 1.9, trailOpacity: .2, impactCount: 12 },
  { id: 'ice-crystal', geometry: mergeGeometries([part(new THREE.OctahedronGeometry(.46, 0), [.7, .7, 1.45]), part(new THREE.OctahedronGeometry(.2, 0), [1, 1, 1], -.55)]), width: .95, length: 1.5, spin: 9, trailLength: 2.1, trailOpacity: .3, impactCount: 14 },
  { id: 'stone-ember', geometry: mergeGeometries([part(new THREE.DodecahedronGeometry(.43, 0), [1, .82, 1.2]), part(new THREE.ConeGeometry(.28, .65, 6), [1, 1, 1], -.48, -Math.PI / 2)]), width: 1.2, length: 1.45, spin: 12, trailLength: 2.4, trailOpacity: .34, impactCount: 13 },
  { id: 'imperial-javelin', geometry: mergeGeometries([part(new THREE.CylinderGeometry(.09, .09, 1.25, 6), [1, 1, 1], 0, Math.PI / 2), part(new THREE.ConeGeometry(.27, .5, 4), [1, 1, 1], .8, Math.PI / 2)]), width: .65, length: 1.9, spin: 0, trailLength: 2.3, trailOpacity: .2, impactCount: 12 },
  { id: 'dragon-flame', geometry: mergeGeometries([part(new THREE.ConeGeometry(.4, .9, 7), [1, 1, 1], .2, Math.PI / 2), part(new THREE.ConeGeometry(.26, .65, 6), [1, 1, 1], -.42, -Math.PI / 2)]), width: 1.2, length: 1.75, spin: 4, trailLength: 2.8, trailOpacity: .38, impactCount: 17 },
  { id: 'clockwork-cog', geometry: mergeGeometries([part(new THREE.TorusGeometry(.38, .1, 6, 12), [1, 1, 1]), part(new THREE.BoxGeometry(.18, .18, 1), [1, 1, 1])]), width: 1.25, length: 1.25, spin: 18, trailLength: 1.8, trailOpacity: .28, impactCount: 15 },
  { id: 'cyber-lance', geometry: mergeGeometries([part(new THREE.BoxGeometry(.28, .22, 1.05), [1, 1, 1]), part(new THREE.OctahedronGeometry(.28, 0), [.75, .75, 1.25], .58)]), width: .75, length: 1.65, spin: 0, trailLength: 2.8, trailOpacity: .42, impactCount: 14 },
  { id: 'hourglass-shard', geometry: mergeGeometries([part(new THREE.TorusGeometry(.38, .065, 6, 18), [1, 1, 1]), part(new THREE.OctahedronGeometry(.28, 0), [.75, .75, 1.4])]), width: 1.35, length: 1.35, spin: -10, trailLength: 2.2, trailOpacity: .36, impactCount: 18 },
];

const VISUALS: BulletVisual[] = ['orb', 'shard', 'ring', 'needle', 'blob', 'star'];
const FALLBACK_LOOKS: EnemyProjectileLook[] = VISUALS.map((visual) => ({
  id: `enemy-${visual}`,
  geometry: bulletGeometry(visual),
  width: 1,
  length: visual === 'needle' ? 1.55 : visual === 'shard' ? 1.3 : 1,
  spin: visual === 'star' ? 16 : visual === 'ring' || visual === 'shard' ? 7 : 0,
  trailLength: visual === 'needle' ? 2 : 1.35,
  trailOpacity: .2,
  impactCount: 9,
}));

export const ENEMY_PROJECTILE_LOOKS = [...ERA_LOOKS, ...FALLBACK_LOOKS] as const;

const ERA_COLORS: readonly (readonly number[])[] = [
  [0xff3fb0, 0x9dff3a], [0xff7a3c, 0xffd23c], [0xff3b3b, 0xff9a1f], [0xffa64d, 0x8fd6ff], [0xff5a2a, 0xff8a2a],
  [0xff3f5f, 0xff7a3f], [0xff3a2a, 0xffa028], [0xffb020, 0xff6a20], [0xff2ad4, 0x9dff3a], [0xff5cf5, 0xffc46a],
];

function distance(a: number, b: number): number {
  const dr = ((a >> 16) & 255) - ((b >> 16) & 255);
  const dg = ((a >> 8) & 255) - ((b >> 8) & 255);
  const db = (a & 255) - (b & 255);
  return dr * dr + dg * dg + db * db;
}

export function enemyProjectileLookIndex(color: number, visual: number, preferredEra = -1): number {
  if (preferredEra >= 0 && preferredEra < ERA_LOOKS.length && (visual === 0 || visual === 4)) return preferredEra;
  let bestEra = -1;
  let best = Infinity;
  ERA_COLORS.forEach((colors, era) => colors.forEach((candidate) => {
    const d = distance(color, candidate);
    if (d < best) { best = d; bestEra = era; }
  }));
  // Authored era colors and close variants get era identity; utility/affix colors retain their shape identity.
  if (bestEra >= 0 && best < 72 * 72) return bestEra;
  return ERA_LOOKS.length + Math.max(0, Math.min(FALLBACK_LOOKS.length - 1, visual));
}

export function enemyProjectileLook(color: number, visual: number, preferredEra = -1): EnemyProjectileLook {
  return ENEMY_PROJECTILE_LOOKS[enemyProjectileLookIndex(color, visual, preferredEra)];
}
